// =============================================================================
// WhatsApp Centralized Inbox API Client
// -----------------------------------------------------------------------------
// Full suite of REST endpoints for WhatsApp configuration, multi-tenant
// inbox conversations, detail panels, outbound replies, assignment controls,
// status lifecycle, unread counter reset, and SSE streams.
// =============================================================================

import { request } from "./client";
import { baseUrl } from "../config/apiConfig";
import { getDataFromLocalStorage } from "../util/localStorage";

// ─── 1. WhatsApp Configuration & Health Badge ──────────────────────────────
export const fetchWhatsAppConfiguration = async (tenantId) => {
  try {
    const response = await request(`/bots/${tenantId}/whatsapp/configuration`, { skipCache: true });
    return response.data || response;
  } catch (error) {
    try {
      const response = await request(`/bots/${tenantId}/whatsapp/status`, { skipCache: true });
      return response.data || response;
    } catch (err) {
      // console.error("[CentralWhatsApp] fetchWhatsAppConfiguration failed:", error);
      return null;
    }
  }
};

// ─── 2. Fetch Tenants List ──────────────────────────────────────────────────
export const fetchTenantsWithWhatsapp = async () => {
  try {
    const response = await request("/bots", { skipCache: true });
    const items = [
      response,
      response?.items,
      response?.bots,
      response?.results,
      response?.data,
      response?.data?.items,
      response?.data?.bots,
      response?.data?.results,
    ].find(Array.isArray) || [];

    return items.map((t) => ({
      id: t.id,
      clientId: t.client_id,
      name: t.name || "Unknown Tenant",
      whatsappNumber: t.display_phone_number || t.phone_number_id || "Not connected",
      status: t.wa_status === "connected" ? "connected" : "disconnected",
      unread: t.unread_count || 0,
    }));
  } catch (error) {
    // console.error("[CentralWhatsApp] fetchTenantsWithWhatsapp failed:", error);
    return [];
  }
};

// ─── 3. Centralized Conversation List ───────────────────────────────────────
export const fetchTenantWhatsappConversations = async (tenantId, params = {}) => {
  try {
    const query = typeof params === "object" ? params : {};
    const response = await request(`/bots/${tenantId}/whatsapp/conversations`, {
      params: query,
      skipCache: true,
    });
    const data = response.data || response;
    if (Array.isArray(data)) {
      return { items: data, total: data.length, page: 1, pages: 1 };
    }
    return {
      items: data.items || [],
      total: data.total || 0,
      page: data.page || 1,
      pages: data.pages || 1,
    };
  } catch (error) {
    // console.error("[CentralWhatsApp] fetchTenantWhatsappConversations failed:", error);
    return { items: [], total: 0, page: 1, pages: 1 };
  }
};

// ─── 4. Conversation Detail Panel (12 fields) ───────────────────────────────
export const fetchWhatsAppConversationDetails = async (tenantId, conversationId) => {
  try {
    const response = await request(`/bots/${tenantId}/whatsapp/conversations/${conversationId}`, { skipCache: true });
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] fetchWhatsAppConversationDetails failed:", error);
    return null;
  }
};

// ─── 5. Chat Messages Stream / List ─────────────────────────────────────────
export const fetchTenantWhatsappMessages = async (tenantId, conversationId, params = {}) => {
  try {
    const query = typeof params === "object" ? params : {};
    const response = await request(
      `/bots/${tenantId}/whatsapp/conversations/${conversationId}/messages`,
      { params: query, skipCache: true }
    );
    const data = response.data || response;
    if (Array.isArray(data)) return data;
    return data.items || data.messages || [];
  } catch (error) {
    // console.error("[CentralWhatsApp] fetchTenantWhatsappMessages failed:", error);
    return [];
  }
};

// ─── 6. Send Outbound Reply ──────────────────────────────────────────────────
export const sendTenantWhatsappReply = async (tenantId, conversationId, payload) => {
  try {
    const bodyData =
      typeof payload === "string"
        ? { msg_type: "text", body: payload }
        : {
            msg_type: payload.msg_type || "text",
            body: payload.body,
            reply_to_message_id: payload.reply_to_message_id,
          };

    const response = await request(
      `/bots/${tenantId}/whatsapp/conversations/${conversationId}/messages`,
      { method: "POST", data: bodyData }
    );
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] sendTenantWhatsappReply failed:", error);
    throw error;
  }
};

// ─── 7. Agent Assignment ─────────────────────────────────────────────────────
export const assignAgentToConversation = async (tenantId, conversationId, userId) => {
  try {
    const endpoint = `/bots/${tenantId}/whatsapp/conversations/${conversationId}/assign`;
    const response = await request(endpoint, {
      method: "PUT",
      data: { user_id: userId },
    });
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] assignAgentToConversation failed:", error);
    throw error;
  }
};

export const unassignAgentFromConversation = async (tenantId, conversationId) => {
  try {
    const endpoint = `/bots/${tenantId}/whatsapp/conversations/${conversationId}/assign`;
    const response = await request(endpoint, {
      method: "DELETE",
    });
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] unassignAgentFromConversation failed:", error);
    throw error;
  }
};

// ─── 8. Mark Read / Reset Unread Counter ─────────────────────────────────────
export const markConversationAsRead = async (tenantId, conversationId) => {
  try {
    const endpoint = `/bots/${tenantId}/whatsapp/conversations/${conversationId}/unread`;
    const response = await request(endpoint, {
      method: "PATCH",
      data: { read: true },
    });
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] markConversationAsRead failed:", error);
    return null;
  }
};

// ─── 9. Change Conversation Lifecycle Status ────────────────────────────────
export const updateConversationStatus = async (tenantId, conversationId, status) => {
  try {
    const endpoint = `/bots/${tenantId}/whatsapp/conversations/${conversationId}/status`;
    const response = await request(endpoint, {
      method: "PATCH",
      data: { status },
    });
    return response.data || response;
  } catch (error) {
    // console.error("[CentralWhatsApp] updateConversationStatus failed:", error);
    throw error;
  }
};

// ─── 10. Real-Time Stream URL Builder ─────────────────────────────────────────
export const getWhatsAppStreamUrl = (tenantId) => {
  const token = getDataFromLocalStorage("token") || "";
  const base = baseUrl.replace(/\/$/, "");
  return `${base}/bots/${tenantId}/whatsapp/stream?token=${encodeURIComponent(token)}`;
};
